import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { SystemSettingValuesSortCol, TSystemSettingList, TSystemSettingValueListResponse, TSystemSettingValueQuery, TSystemSettingValuesSortCol, adaptSystemSettingValueFromListResponse, adaptSystemSettingValuesQueryToRequest } from '../../types';
import { BACKEND_ADMIN_SYSTEM_SETTINGS_CALENDAR } from '../../backend-paths';


export const systemSettingCalendarGetApi = createAsyncThunk(
	BACKEND_ADMIN_SYSTEM_SETTINGS_CALENDAR,
	(payload: TSystemSettingValueQuery | undefined, thunkApi) => {
		const params = adaptSystemSettingValuesQueryToRequest(payload);

		return HttpService.get<TSystemSettingValueListResponse>(BACKEND_ADMIN_SYSTEM_SETTINGS_CALENDAR, { params }).then(result => {
			return adaptSystemSettingValueFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TSystemSettingList;
	query: TSystemSettingValueQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: SystemSettingValuesSortCol.CREATED_AT,
	},
	error: undefined,
};

export const systemSettingCalendarSlice = createSlice({
	name: 'systemSettingCalendarSlice',
	initialState,
	reducers: {
		setSystemSettingCalendarQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setSystemSettingCalendarQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setSystemSettingCalendarQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setSystemSettingCalendarQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setSystemSettingCalendarQuerySortCol(state, action: PayloadAction<TSystemSettingValuesSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearSystemSettingCalendar(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[systemSettingCalendarGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[systemSettingCalendarGetApi.fulfilled.type]: (state, action: PayloadAction<TSystemSettingList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[systemSettingCalendarGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useSystemSettingCalendarSelector = () => useAppSelector(state => state[systemSettingCalendarSlice.name]);

export const { clearSystemSettingCalendar, setSystemSettingCalendarQueryLimit, setSystemSettingCalendarQueryOffset, setSystemSettingCalendarQuerySort, setSystemSettingCalendarQuerySortCol, setSystemSettingCalendarQuerySearch } = systemSettingCalendarSlice.actions;
